<script setup lang="ts">
import { PopoverArrow } from "reka-ui";
import { computed, nextTick, onBeforeUnmount, provide, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useBreakpoint } from "@/design/composables/use-breakpoint";
import Popover from "@/design/primitives/Popover.vue";
import BenOrb, { type OrbState } from "@/shared/agent-display/BenOrb.vue";
import type { NavigateTarget } from "../api/stream";
import { useBen } from "../api/use-ben";
import { DOCK_CLOSE } from "../composables/dock-close";
import { useAgentsI18n } from "../i18n/use-agents-i18n";
import BenDock from "./BenDock.vue";

/**
 * A GAVETA do Ben: o orbe, a borda que se arrasta, e nada sobre a conversa.
 *
 * O que este arquivo é depois do corte: a JANELA. Onde ela se abre, que largura
 * tem, se ocupa a tela, e o orbe que a chama — que continua a respirar com ela
 * fechada, porque é o único sinal de que um turno longo ainda está vivo depois de
 * alguém se afastar dele.
 *
 * Quem é o Ben, em que conversa ele está e o que se diz lá dentro é `BenDock.vue`,
 * a mesma peça que a coluna da caixa de entrada monta. Enquanto eram duas, a
 * caixa de entrada ficava com a metade pobre.
 *
 * Uma GAVETA e não a caixinha flutuante que era: as respostas do Ben são tabelas,
 * listas e código, e um cartão de 24rem transformava cada uma numa coluna de
 * fragmentos — a razão de ele ler como brinquedo ao lado da V2. Altura toda na
 * lateral de `md` para cima, tela inteira abaixo disso, porque 380px de conversa
 * sobre um telefone de 360px não deixa nada para nenhum dos dois.
 */
const props = defineProps<{
	/** A jornada do primeiro dia está à vista: é a hora de apresentar a orbe, uma vez. */
	teach?: boolean;
	topbar?: boolean;
}>();
const emit = defineEmits<{ presentation: [state: { open: boolean; wide: boolean }] }>();
const { isMobile } = useBreakpoint();
const { t } = useAgentsI18n();
const router = useRouter();

const open = ref(false);
provide(DOCK_CLOSE, () => {
	open.value = false;
});
let returnFocus: HTMLElement | null = null;
watch(open, async (shown) => {
	if (shown) {
		returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
		await nextTick();
		drawer.value?.querySelector<HTMLTextAreaElement>("textarea")?.focus();
	} else if (returnFocus?.isConnected) returnFocus.focus();
});

/** The topbar introduction is remembered separately from the retired floating launcher. */
const TAUGHT_KEY = "benvox.orb.topbar.taught";
const taught = ref(readTaught());
const celebrating = ref(false);
let celebrationTimer: ReturnType<typeof setTimeout> | undefined;
onBeforeUnmount(() => clearTimeout(celebrationTimer));
function readTaught(): boolean {
	try {
		return localStorage.getItem(TAUGHT_KEY) === "1";
	} catch {
		return false;
	}
}
function markTaught(): void {
	taught.value = true;
	try {
		localStorage.setItem(TAUGHT_KEY, "1");
	} catch {
		// Sem armazenamento o balão volta noutra visita; nada se perde.
	}
}
const showTip = computed(
	() => celebrating.value || (!!props.teach && !taught.value && !open.value),
);

/**
 * O BALÃO DESCE ATÉ ABAIXO DA FAIXA DO ONBOARDING (19/10).
 *
 * Ele nasce colado ao orbe, e logo abaixo do cabeçalho mora a faixa "Etapa 1 de 5"
 * — que ele tapava, justamente no tour que manda lê-la. A distância é medida na
 * hora (a faixa tem uma linha no telefone e três no computador) e vai como variável
 * na raiz, porque o painel é teleportado para o `body` e não herda nada daqui.
 */
const launcher = ref<HTMLElement | null>(null);
watch(
	showTip,
	async (shown) => {
		const root = document.documentElement.style;
		if (!shown) {
			root.removeProperty("--orb-tip-drop");
			return;
		}
		await nextTick();
		const band = document.querySelector('[data-testid="journey-banner"]')?.closest("section");
		const orb = launcher.value?.getBoundingClientRect().bottom ?? 0;
		const drop = band ? Math.max(0, band.getBoundingClientRect().bottom - orb) : 0;
		root.setProperty("--orb-tip-drop", `${Math.round(drop)}px`);
	},
	{ immediate: true },
);
onBeforeUnmount(() => document.documentElement.style.removeProperty("--orb-tip-drop"));

/**
 * A JANELA OCUPANDO A TELA — o modo de vista que a V3 não tinha.
 *
 * A V2 tem três (gaveta, flutuante, tela cheia); aqui são dois, e de propósito.
 * O que a gaveta não resolve é LARGURA de conteúdo: uma tabela de nove colunas
 * ou um bloco de código numa faixa de 30rem sai quebrado, e a resposta boa vira
 * a resposta ilegível. O split já dá o ajuste fino; isto dá o gesto de uma
 * tecla. O flutuante arrastável ficou fora — um terceiro estado na mesma doca
 * que se abre nas duas portas seria mais coisa a lembrar do que a ganhar.
 *
 * Lembrado entre visitas, como a largura: quem trabalha em tela cheia trabalha
 * em tela cheia amanhã também.
 */
const WIDE_KEY = "benvox.dock.wide";
const wide = ref(readWide());
watch([open, wide], () => emit("presentation", { open: open.value, wide: wide.value }));
function toggleChat(): void {
	open.value = !open.value;
}

function readWide(): boolean {
	try {
		return localStorage.getItem(WIDE_KEY) === "1";
	} catch {
		return false;
	}
}

function toggleWide(): void {
	wide.value = !wide.value;
	try {
		localStorage.setItem(WIDE_KEY, wide.value ? "1" : "0");
	} catch {
		// Sem quota não se lembra o modo; a janela desta visita já mudou.
	}
}

/**
 * A borda esquerda do drawer é um split de verdade: arrasta e a conversa fica
 * com a largura que a pessoa quiser, lembrada entre sessões.
 *
 * Era `w-[min(30rem,42vw)]` fixo — no monitor largo a resposta em tabela vivia
 * espremida em 480px sem remédio (dono, 20/08: "ben não move split lateral").
 * `null` é "nunca arrastou": aí vale a largura padrão de sempre, que continua
 * responsiva por ser em vw. Pointer capture no próprio handle, como o resto dos
 * arrastos da casa, para o dedo poder sair da linha de 6px sem soltar o split.
 */
const DOCK_WIDTH_KEY = "benvox.dock.width";
const MIN_DOCK_WIDTH = 320;
const drawer = ref<HTMLElement | null>(null);
const dockWidth = ref<number | null>(readDockWidth());
const resizing = ref(false);
let splitStart: { x: number; width: number } | null = null;

function readDockWidth(): number | null {
	try {
		const saved = Number(localStorage.getItem(DOCK_WIDTH_KEY));
		return Number.isFinite(saved) && saved >= MIN_DOCK_WIDTH ? saved : null;
	} catch {
		return null;
	}
}

/** Nunca menor que uma conversa legível, nunca maior que 80% da janela. */
function clampDockWidth(px: number): number {
	return Math.min(Math.max(px, MIN_DOCK_WIDTH), Math.round(window.innerWidth * 0.8));
}

/**
 * Em tela cheia a largura arrastada não vale: ela É a janela inteira.
 *
 * O recuo do teclado vai SEMPRE, nas duas larguras: a gaveta é `h-dvh`, e no
 * iPhone o `dvh` não sabe do teclado — o compositor, que vive no fundo dela,
 * ficava debaixo dele. Onde a janela já encolhe sozinha (`interactive-widget`,
 * ver `index.html`) a variável vale zero e isto não faz nada. Ver
 * `use-keyboard-inset.ts`.
 */
const dockStyle = computed(() => ({
	paddingBottom: "var(--kb-inset, 0px)",
	...(!isMobile.value && !wide.value && dockWidth.value !== null
		? { width: `${dockWidth.value}px` }
		: {}),
}));

function onSplitDown(event: PointerEvent): void {
	if (event.button !== 0) return;
	(event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
	splitStart = {
		x: event.clientX,
		width: drawer.value?.getBoundingClientRect().width ?? MIN_DOCK_WIDTH,
	};
	resizing.value = true;
}

function onSplitMove(event: PointerEvent): void {
	if (!splitStart) return;
	// Mover para a ESQUERDA alarga: o drawer é ancorado na direita.
	dockWidth.value = clampDockWidth(splitStart.width + (splitStart.x - event.clientX));
}

function onSplitUp(): void {
	if (!splitStart) return;
	splitStart = null;
	resizing.value = false;
	try {
		if (dockWidth.value !== null)
			localStorage.setItem(DOCK_WIDTH_KEY, String(Math.round(dockWidth.value)));
	} catch {
		// Sem quota não se lembra a largura; o arrasto em si já aconteceu.
	}
}

/** Duplo clique no split volta à largura padrão — o "desfazer" mais barato que há. */
function onSplitReset(): void {
	dockWidth.value = null;
	try {
		localStorage.removeItem(DOCK_WIDTH_KEY);
	} catch {
		// Idem.
	}
}

/**
 * Com quem a gaveta fala — a ESCOLHA, que é tudo o que sobrou aqui.
 *
 * Quem resolve a escolha em agente (e quem cai para outro quando o código
 * lembrado foi desligado ou apagado) é o servidor, na mesma resposta que a doca
 * lê. Esta gaveta guarda-a porque também a recebe de fora, pelo `openWith` das
 * páginas, e porque é ela que desenha o orbe com o nome de quem está lá dentro.
 *
 * A mesma pergunta que o `BenDock` faz, com a mesma chave: uma consulta, um
 * pedido, duas leitoras do mesmo cache. Duas REGRAS é que era o defeito.
 */
const chosenCode = ref<string | null>(null);
const noSubject = ref(null);
const { data: atDoor } = useBen({ subject: noSubject, preferred: chosenCode });
const ben = computed(() => atDoor.value?.ben ?? null);

const orbState = ref<OrbState>("idle");

/**
 * Working while closed still shows.
 *
 * Someone asks Ben for something, closes the drawer and keeps working — the orb
 * is then the only evidence the turn is still running, and freezing it would
 * make a long turn look like a crash.
 */
const dockState = computed<OrbState>(() => orbState.value);

/** Abrir e fechar, e mais nada: em que conversa a doca está é do `BenDock`. */
function toggle(): void {
	open.value = !open.value;
}

/** A doca por dentro, para o `prefill` de quem chega por `openWith`. */
const dockRef = ref<{
	prefill: (text: string, send?: boolean) => void;
	startNew: () => void;
} | null>(null);

/**
 * A porta que as páginas usam ("Pedir ao Ben", click-to-edit do site): abre a
 * gaveta já no agente pedido e, se vier texto, escreve no compositor SEM enviar.
 *
 * Se o agente pedido ainda não chegou na lista, o código fica guardado — o
 * servidor resolve-o na resposta seguinte, e cai para outro se ele não existir.
 */
function openWith(code?: string, prefill?: string, fresh?: boolean, send?: boolean): void {
	if (code) chosenCode.value = code;
	if (!open.value) toggle();
	// Antes do `prefill`: a conversa nova remonta o compositor, e a frase tem de
	// cair no compositor NOVO — o `nextTick` abaixo já corre depois dessa troca.
	if (fresh) dockRef.value?.startNew();
	if (prefill) void nextTick(() => dockRef.value?.prefill(prefill, send));
}

// Aberto ou fechado é estado desta visita, controlado pela doca.
defineExpose({ toggle, toggleChat, openWith });

function onLauncherClick(): void {
	if (showTip.value && !celebrating.value) {
		markTaught();
		celebrating.value = true;
		celebrationTimer = setTimeout(() => (celebrating.value = false), 2200);
	}
	toggleChat();
}

/**
 * Levar a pessoa aonde o agente disse que ia.
 *
 * `router.push` e não `window.location`: a gaveta vive FORA do `<RouterView>`,
 * então a conversa continua exatamente onde estava enquanto a tela por baixo
 * troca — e o "voltar" do navegador devolve a tela anterior com a conversa ainda
 * aberta.
 *
 * O caminho vem pronto do servidor (a busca da fase 3 já o monta); aqui não se
 * concatena rota nenhuma.
 */
function go(target: NavigateTarget): void {
	void router.push(target.to);
}
</script>

<template>
	<template v-if="ben">
		<div v-if="open && !wide && !isMobile" class="fixed inset-0 z-40 bg-black/40" @click="open = false" />
		<!-- Hidden rather than unmounted, so an open conversation survives a page
		     change. Ben is meant to be a companion to what you are doing, not a
		     place you go. -->
		<div
			v-show="open"
			ref="drawer"
			class="fixed top-0 right-0 z-50 flex h-dvh flex-col border-l border-border bg-surface shadow-2xl"
			:class="[
				isMobile || wide ? 'w-full' : dockWidth === null ? 'w-[min(30rem,42vw)]' : '',
				// Sem seleção DURANTE o arrasto, ou cada ajuste de largura sai
				// pintando o texto da conversa atrás do ponteiro.
				resizing ? 'select-none' : '',
			]"
			:style="dockStyle"
			role="dialog"
			:aria-label="ben.name"
			@keydown.esc="open = false"
		>
			<!-- O split: dez pixels de ALVO, mas o que se vê é só o tracinho no meio,
			     como na V2 — a faixa inteira pintada lia como uma borda grossa, e
			     borda não convida a arrastar. O tracinho fica no cinza do painel e
			     só ganha o azul quando o ponteiro chega ou o arrasto está em curso. -->
			<div
				v-if="!isMobile && !wide"
				class="group absolute inset-y-0 left-0 z-10 flex w-2.5 cursor-col-resize items-center justify-center"
				:style="{ touchAction: 'none' }"
				role="separator"
				aria-orientation="vertical"
				:aria-label="t('dock.resize')"
				@pointerdown="onSplitDown"
				@pointermove="onSplitMove"
				@pointerup="onSplitUp"
				@pointercancel="onSplitUp"
				@dblclick="onSplitReset"
			>
				<span
					class="h-9 w-[3px] rounded-full transition-colors"
					:class="resizing ? 'bg-accent' : 'bg-border group-hover:bg-accent'"
					aria-hidden="true"
				/>
			</div>
			<BenDock
				ref="dockRef"
				class="min-h-0 flex-1"
				:preferred="chosenCode"
				:wide="wide"
				widenable
				@state="orbState = $event"
				@navigate="go"
				@toggle-wide="toggleWide"
				@close="open = false"
			/>
		</div>

		<!-- `defer`: o lugar na barra é desenhado pelo AppHeader no mesmo render; sem isto a orbe montava antes dele e ficava sem casa até o F5 (16/10). -->
		<Teleport defer to="#ben-launcher" :disabled="!topbar">
			<Popover :open="showTip" :label="t('dock.tipTitle', { name: ben.name })" anchor align="end" :panel-class="celebrating ? 'mt-(--orb-tip-drop) w-64 border-success bg-success-subtle p-3 text-sm text-fg' : 'mt-(--orb-tip-drop) w-64 border-accent p-3 text-sm text-fg'">
                <template #trigger>
				<button
					ref="launcher"
					type="button"
					class="flex size-10 items-center justify-center rounded-full transition-transform hover:scale-105 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
					:class="[open && wide ? 'bg-bar-hover' : '', showTip ? 'ring-2 ring-accent ring-offset-2 ring-offset-surface' : '']"
					:aria-expanded="open"
					:aria-label="t('dock.launcher', { name: ben.name })"
					:title="`${t('dock.launcher', { name: ben.name })} · Ctrl/⌘ + J`"
					@click="onLauncherClick"
				>
					<BenOrb :state="celebrating ? 'success' : dockState" :size="26" aria-hidden="true" />
				</button>
                </template>
				<div
					role="status"
					data-testid="orb-tip"
				>
					<template v-if="celebrating">{{ t("dock.tipDone") }}</template>
					<template v-else>
						<p class="font-semibold">{{ t("dock.tipTitle", { name: ben.name }) }}</p>
						<p class="mt-1 text-fg-muted">{{ t("dock.tipBody") }}</p>
						<p class="mt-3 rounded-md bg-bg p-2 text-xs text-fg-muted">{{ t("dock.tipShortcut") }}</p>
						<button type="button" class="mt-2 text-xs text-accent underline" @click="markTaught">{{ t("dock.tipDismiss") }}</button>
					</template>
				</div>
                <PopoverArrow as-child :width="28" :height="28">
                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" :class="celebrating ? 'text-success' : 'text-accent'" aria-hidden="true">
                        <path d="M14 1V25M5 16L14 25L23 16" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </PopoverArrow>
			</Popover>
		</Teleport>
	</template>
</template>
